import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Menu, X, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
const topics = [{
  id: 'tips',
  title: 'Air conditioning maintenance tips'
}, {
  id: 'contracts',
  title: 'Commercial AC maintenance contracts'
}, {
  id: 'reports',
  title: 'Sample AC service report'
}, {
  id: 'filters',
  title: 'AC filters: types and cleaning'
}, {
  id: 'winter',
  title: 'Winter AC heating maintenance'
}];
export function MaintenanceGuide() {
  const [activeId, setActiveId] = useState<string>('');
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    topics.forEach(({
      id
    }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileTocOpen(false);
  };
  return <div className="min-h-screen bg-white font-sans text-primary-dark" data-id="element-1619">
      <section className="bg-[#E8F0F8] pt-32 pb-20" data-id="element-1620">
        <div className="container mx-auto px-6" data-id="element-1621">
          <div className="max-w-4xl" data-id="element-1622">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-dark/60 mb-6" data-id="element-1623">
              <Link to="/knowledge-hub" className="hover:text-accent transition-colors" data-id="element-1624">
                Resources
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-1625" />
              <span className="text-primary-dark" data-id="element-1626">Maintenance Guide</span>
            </div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-1627">
              Commercial AC maintenance guide
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-1628">
              Extend the lifespan of your HVAC systems, reduce energy bills by
              30%, and prevent costly breakdowns with our expert maintenance
              strategies. Published April 2026.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16" data-id="element-1629">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-1630">
          <div className="lg:hidden" data-id="element-1631">
            <button onClick={() => setIsMobileTocOpen(!isMobileTocOpen)} className="flex items-center gap-2 text-primary-dark font-medium w-full justify-between bg-[#E8F0F8] p-4 rounded-xl" data-id="element-1632">
              <span data-id="element-1633">Table of contents</span>
              {isMobileTocOpen ? <X className="w-5 h-5" data-id="element-1634" /> : <Menu className="w-5 h-5" data-id="element-1635" />}
            </button>
            {isMobileTocOpen && <div className="mt-2 bg-white border border-primary-dark/10 rounded-xl p-4 shadow-lg" data-id="element-1636">
                <nav className="flex flex-col gap-3" data-id="element-1637">
                  {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/70 hover:text-primary-dark'}`} data-id="element-1638">
                      {topic.title}
                    </button>)}
                </nav>
              </div>}
          </div>

          <aside className="hidden lg:block w-64 shrink-0" data-id="element-1639">
            <div className="sticky top-32" data-id="element-1640">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary-dark/50 mb-6" data-id="element-1641">
                Contents
              </h3>
              <nav className="flex flex-col gap-4 border-l-2 border-primary-dark/10 pl-4" data-id="element-1642">
                {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors relative ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/60 hover:text-primary-dark'}`} data-id="element-1643">
                    {activeId === topic.id && <motion.div layoutId="activeToc" className="absolute -left-[18px] top-1/2 -translate-y-1/2 w-0.5 h-full bg-accent" data-id="element-1644" />}
                    {topic.title}
                  </button>)}
              </nav>
            </div>
          </aside>

          <main className="flex-1 max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:text-primary-dark prose-p:text-primary-dark/80 prose-a:text-accent prose-li:text-primary-dark/80" data-id="element-1645">
            {/* Topic 1 */}
            <section id="tips" className="mb-20 scroll-mt-32" data-id="element-1646">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1647">
                Air conditioning maintenance tips
              </h2>
              <p data-id="element-1648">
                Regular AC maintenance extends system life to 15+ years and can
                save up to 30% on energy consumption. While professional
                servicing is required for commercial use, there are steps
                facility managers can take.
              </p>
              <p data-id="element-1649">
                Coventry tips: Schedule major services in spring and autumn to
                avoid peak summer and winter rush periods.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1650">
                Top 10 maintenance tips
              </h3>
              <ol className="list-decimal pl-6 mb-8 space-y-2" data-id="element-1651">
                <li data-id="element-1652">
                  Replace or clean filters quarterly (or monthly in dusty
                  environments).
                </li>
                <li data-id="element-1653">
                  Clear outdoor condenser coils annually to ensure proper
                  airflow.
                </li>
                <li data-id="element-1654">
                  Monitor refrigerant levels (must be done by an F-Gas
                  professional).
                </li>
                <li data-id="element-1655">
                  Use smart timers to ensure systems are off during non-working
                  hours.
                </li>
                <li data-id="element-1656">Test thermostats regularly for accuracy.</li>
                <li data-id="element-1657">
                  Keep the area around outdoor units clear of debris and
                  vegetation.
                </li>
                <li data-id="element-1658">
                  Check condensate drains for blockages to prevent water damage.
                </li>
                <li data-id="element-1659">
                  Listen for unusual noises which indicate failing bearings or
                  belts.
                </li>
                <li data-id="element-1660">Inspect ductwork for leaks or poor insulation.</li>
                <li data-id="element-1661">
                  Schedule professional preventative maintenance at least twice
                  a year.
                </li>
              </ol>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1662">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1663">Maintenance FAQs</h3>
                <div className="space-y-4" data-id="element-1664">
                  {[{
                  q: 'What are the signs of poor maintenance?',
                  a: 'High energy bills, weak airflow, foul smells, and inconsistent temperatures are the first signs.'
                }, {
                  q: 'How much does a service cost?',
                  a: 'Commercial servicing in Coventry starts from £100+VAT per visit, depending on system size.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1665">
                      <button onClick={() => setOpenFaqId(openFaqId === `tips-${i}` ? null : `tips-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1666">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1667">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1668">
                          {openFaqId === `tips-${i}` ? <Minus className="w-5 h-5" data-id="element-1669" /> : <Plus className="w-5 h-5" data-id="element-1670" />}
                        </span>
                      </button>
                      {openFaqId === `tips-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1671">
                          <p itemProp="text" data-id="element-1672">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 2 */}
            <section id="contracts" className="mb-20 scroll-mt-32" data-id="element-1673">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1674">
                Commercial AC maintenance contracts
              </h2>
              <p data-id="element-1675">
                A formal maintenance contract ensures you meet F-Gas and{' '}
                <Link to="/knowledge-hub/compliance" data-id="element-1676">TM44 compliance</Link>{' '}
                automatically. It shifts the burden of scheduling and
                record-keeping to your HVAC partner.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1677">
                Maintenance packages
              </h3>
              <div className="overflow-x-auto mb-8" data-id="element-1678">
                <table className="w-full text-left border-collapse" data-id="element-1679">
                  <thead data-id="element-1680">
                    <tr className="bg-[#E8F0F8]" data-id="element-1681">
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1682">
                        Package
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1683">
                        Visits/Year
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1684">
                        Free Callouts
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1685">
                        Best For
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1686">
                    <tr data-id="element-1687">
                      <td className="p-4 border-b border-primary-dark/10 font-medium" data-id="element-1688">
                        Standard
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1689">2</td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1690">
                        None
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1691">
                        Light use retail/small offices
                      </td>
                    </tr>
                    <tr data-id="element-1692">
                      <td className="p-4 border-b border-primary-dark/10 font-medium" data-id="element-1693">
                        Premium
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1694">3</td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1695">1</td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1696">
                        Medium offices, restaurants
                      </td>
                    </tr>
                    <tr data-id="element-1697">
                      <td className="p-4 border-b border-primary-dark/10 font-medium" data-id="element-1698">
                        Platinum
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1699">4</td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1700">
                        Unlimited
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1701">
                        Critical environments, server rooms
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1702">
                <h4 className="text-xl font-serif mb-4" data-id="element-1703">
                  Compare maintenance packages
                </h4>
                <p className="mb-6 text-base" data-id="element-1704">
                  Find the right level of cover for your commercial premises.
                </p>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1705">
                  Get a contract quote
                  <ChevronRight className="w-4 h-4" data-id="element-1706" />
                </Link>
              </div>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1707">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1708">Contract FAQs</h3>
                <div className="space-y-4" data-id="element-1709">
                  {[{
                  q: 'Do contracts include mandatory leak checks?',
                  a: "Yes, all our contracts are tailored to ensure you meet F-Gas leak check regulations based on your system's charge."
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1710">
                      <button onClick={() => setOpenFaqId(openFaqId === `contracts-${i}` ? null : `contracts-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1711">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1712">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1713">
                          {openFaqId === `contracts-${i}` ? <Minus className="w-5 h-5" data-id="element-1714" /> : <Plus className="w-5 h-5" data-id="element-1715" />}
                        </span>
                      </button>
                      {openFaqId === `contracts-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1716">
                          <p itemProp="text" data-id="element-1717">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 3 */}
            <section id="reports" className="mb-20 scroll-mt-32" data-id="element-1718">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1719">
                Sample AC service report
              </h2>
              <p data-id="element-1720">
                A high-quality service report is essential for FM directors and
                compliance audits. AccuTemp provides detailed, digitised reports
                after every visit.
              </p>
              <p data-id="element-1721">Our reports include:</p>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-1722">
                <li data-id="element-1723">Photos before and after cleaning.</li>
                <li data-id="element-1724">Refrigerant log updates.</li>
                <li data-id="element-1725">Efficiency scores and temperature deltas.</li>
                <li data-id="element-1726">
                  Clear recommendations for future repairs to prevent
                  breakdowns.
                </li>
              </ul>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1727">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1728">Report FAQs</h3>
                <div className="space-y-4" data-id="element-1729">
                  {[{
                  q: 'Can reports be custom branded?',
                  a: 'Yes, for our M&E contractor partners, we offer fully white-labelled service reports.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1730">
                      <button onClick={() => setOpenFaqId(openFaqId === `reports-${i}` ? null : `reports-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1731">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1732">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1733">
                          {openFaqId === `reports-${i}` ? <Minus className="w-5 h-5" data-id="element-1734" /> : <Plus className="w-5 h-5" data-id="element-1735" />}
                        </span>
                      </button>
                      {openFaqId === `reports-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1736">
                          <p itemProp="text" data-id="element-1737">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 4 */}
            <section id="filters" className="mb-20 scroll-mt-32" data-id="element-1738">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1739">
                AC filters: types and cleaning
              </h2>
              <p data-id="element-1740">
                Dirty filters are the number one cause of AC breakdowns. They
                restrict airflow, causing the compressor to overwork and
                eventually fail. Cleaning filters monthly can boost airflow by
                up to 25%.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1741">Filter types</h3>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-1742">
                <li data-id="element-1743">
                  <strong data-id="element-1744">Pleated filters:</strong> Better surface area,
                  captures more dust. Standard for commercial units.
                </li>
                <li data-id="element-1745">
                  <strong data-id="element-1746">Pocket filters:</strong> Used in larger AHUs (Air
                  Handling Units) for high-volume filtration.
                </li>
                <li data-id="element-1747">
                  <strong data-id="element-1748">HEPA filters:</strong> Essential for healthcare or
                  environments requiring strict air quality control.
                </li>
              </ul>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1749">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1750">Filter FAQs</h3>
                <div className="space-y-4" data-id="element-1751">
                  {[{
                  q: 'What type of filter is best for allergies?',
                  a: 'HEPA filters are best as they capture 99.97% of particles, including pollen and fine dust.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1752">
                      <button onClick={() => setOpenFaqId(openFaqId === `filters-${i}` ? null : `filters-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1753">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1754">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1755">
                          {openFaqId === `filters-${i}` ? <Minus className="w-5 h-5" data-id="element-1756" /> : <Plus className="w-5 h-5" data-id="element-1757" />}
                        </span>
                      </button>
                      {openFaqId === `filters-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1758">
                          <p itemProp="text" data-id="element-1759">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 5 */}
            <section id="winter" className="mb-20 scroll-mt-32" data-id="element-1760">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1761">
                Winter AC heating maintenance
              </h2>
              <p data-id="element-1762">
                Modern commercial air conditioning systems are heat pumps,
                meaning they provide highly efficient heating in winter.
                However, winter operation puts different stresses on the system.
              </p>
              <p data-id="element-1763">
                Key winter maintenance includes checking the defrost cycle. If
                the outdoor unit cannot properly defrost, ice builds up and can
                crack the fan blades or damage the coil.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1764">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1765">
                  Winter heating FAQs
                </h3>
                <div className="space-y-4" data-id="element-1766">
                  {[{
                  q: 'Are all AC units reversible for heating?',
                  a: 'Approximately 90% of modern commercial AC units are heat pumps capable of both heating and cooling.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1767">
                      <button onClick={() => setOpenFaqId(openFaqId === `winter-${i}` ? null : `winter-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1768">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1769">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1770">
                          {openFaqId === `winter-${i}` ? <Minus className="w-5 h-5" data-id="element-1771" /> : <Plus className="w-5 h-5" data-id="element-1772" />}
                        </span>
                      </button>
                      {openFaqId === `winter-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1773">
                          <p itemProp="text" data-id="element-1774">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
}